import { ComponentType, ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  Component,
  DestroyRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
  InjectionToken,
  Injector,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, Subject } from 'rxjs';

export const TT_TOOLTIP_DELAY = new InjectionToken<number>('The delay in ms before the tooltip is shown');
const TT_TOOLTIP_CONTENT = new InjectionToken<string>('The text content of the tooltip');

const POSITION_MAPPER: { [key: string]: ConnectedPosition } = {
  top: {
    overlayX: 'center',
    overlayY: 'bottom',
    originX: 'center',
    originY: 'top',
    offsetY: -10,
  },
  bottom: {
    overlayX: 'center',
    overlayY: 'top',
    originX: 'center',
    originY: 'bottom',
    offsetY: 10,
  },
  left: {
    overlayX: 'end',
    overlayY: 'center',
    originX: 'start',
    originY: 'center',
    offsetX: -10,
  },
  right: {
    overlayX: 'start',
    overlayY: 'center',
    originX: 'end',
    originY: 'center',
    offsetX: 10,
  },
};

/**
 * A directive that shows a tooltip when the user hovers over the host element.

 * Usage:
 * ```html
 *   <button [ttTooltip]="MyTooltipComponent">Hover me</button>
 * ```
 *
 * The tooltip is shown after a delay of `ttTooltipDelay` ms. The delay can be configured by providing a value for the `TT_TOOLTIP_DELAY` injection token.
 * A value of 300 ms is used by default.
 *
 * Example:
 * ```typescript
 * { provide: TT_TOOLTIP_DELAY, useValue: 500 }
 * ```
 */
@Directive({
  selector: '[ttTooltip]',
  standalone: true,
})
export class TooltipDirective<T extends ComponentType<any> | string> implements OnDestroy, OnInit {
  private readonly overlayService = inject(Overlay);
  private readonly elementRef = inject(ElementRef);
  private readonly ttTooltipDelay = inject(TT_TOOLTIP_DELAY, { optional: true }) ?? 300;
  private readonly destroyRef = inject(DestroyRef);

  private readonly mouseEnterDelaySubject = new Subject<void>();

  private mouseIn = false;

  @Input('ttTooltip') public tooltip: T | null;

  @Input('ttTooltipDisabled') public disabled = false;

  @Input('ttTooltipDelay') public delay = this.ttTooltipDelay;

  @Input('ttTooltipPositionKey') public positionKey: 'top' | 'bottom' | 'left' | 'right' | undefined;

  @Input('ttTooltipWidth') public width: string | null = null;

  private overlayRef: OverlayRef;

  public ngOnInit() {
    this.mouseEnterDelaySubject
      .pipe(debounceTime(this.delay), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.mouseIn && this.openPopover());
  }

  @HostListener('mouseenter') public onMouseEnter(): void {
    if (!this.tooltip || this.disabled) {
      return;
    }

    this.mouseEnterDelaySubject.next();
    this.mouseIn = true;
  }

  @HostListener('mouseleave') public onMouseLeave(): void {
    this.closeOverlay();
    this.mouseIn = false;
  }

  private openPopover(): void {
    if (this.overlayRef) {
      return;
    }

    this.overlayRef = this.overlayService.create({
      positionStrategy: this.overlayService
        .position()
        .flexibleConnectedTo(this.elementRef)
        .withPositions(
          this.positionKey && POSITION_MAPPER[this.positionKey]
            ? [
                POSITION_MAPPER[this.positionKey],
                POSITION_MAPPER['top'],
                POSITION_MAPPER['bottom'],
                POSITION_MAPPER['left'],
                POSITION_MAPPER['right'],
              ]
            : [POSITION_MAPPER['top'], POSITION_MAPPER['bottom'], POSITION_MAPPER['left'], POSITION_MAPPER['right']],
        ),
      scrollStrategy: this.overlayService.scrollStrategies.reposition(),
    });

    let componentPortal: ComponentPortal<any>;

    if (typeof this.tooltip === 'string') {
      componentPortal = new ComponentPortal(
        TooltipComponent,
        null,
        Injector.create({
          providers: [
            {
              provide: TT_TOOLTIP_CONTENT,
              useValue: this.tooltip,
            },
          ],
        }),
      );
    } else {
      componentPortal = new ComponentPortal(this.tooltip);
    }

    const tooltipRef = this.overlayRef.attach(componentPortal);

    if (tooltipRef.instance) {
      tooltipRef.instance.width = this.width;
    }
  }

  private closeOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  public ngOnDestroy() {
    this.closeOverlay();
  }
}

@Component({
  selector: 'tt-tooltip',
  styleUrls: ['./tooltip.directive.scss'],
  template: `
    <div
      data-testid="tooltip-generic"
      [style.display]="width ? 'flex' : null"
      [style.justifyContent]="width ? 'center' : null"
      [style.width]="width">
      {{ text }}
    </div>
  `,
  standalone: true,
})
class TooltipComponent {
  protected text = inject(TT_TOOLTIP_CONTENT);

  @Input() public width: string | null = null;
}
